import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { CSSObject } from 'styled-components';

import { Theme } from '../../config/theme';
import { ContentLoader } from '../ui/loaders/ContentLoader';

interface Props {
  isOwner?: boolean;
  photoUrl?: string | null;
  onClick?: () => void;
}

export const ProfileImageComponent: React.FC<Props> = ({
  isOwner,
  photoUrl,
  onClick,
  ...otherProps
}) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const { t } = useTranslation();

  const handleClick = () => {
    if (!isOwner) return;
    typeof onClick === 'function' && onClick();
  };

  const renderContent = () => {
    if (photoUrl === null || photoUrl === '' || error) {
      return (
        <NoImagePlaceholder onClick={handleClick} isOwner={!!isOwner} {...otherProps}>
          {isOwner && (
            <SetYourNft className='set-your-nft' isPlaceholder>
              {t('profilePage.setYourNft')}
            </SetYourNft>
          )}
        </NoImagePlaceholder>
      );
    }

    return (
      <>
        <Image
          imgUrl={loaded && photoUrl ? photoUrl : null}
          isOwner={!!isOwner}
          onClick={handleClick}
          {...otherProps}
        >
          {!loaded && (
            <ContentLoader viewBox='0 0 200 200'>
              <rect x='0' y='0' rx='50%' height={200} width={200} />
            </ContentLoader>
          )}
          {isOwner && (
            <SetYourNft className='set-your-nft'>
              {t('profilePage.setYourNft')}
            </SetYourNft>
          )}
        </Image>
        {photoUrl && !loaded && (
          <img
            src={photoUrl}
            alt='dsad'
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
            style={{ visibility: 'hidden', width: 1, height: 1, position: 'absolute' }}
          />
        )}
      </>
    );
  };

  return <>{renderContent()}</>;
};

const Image = styled('div')<{ isOwner: boolean; imgUrl: string | null }>(
  ({ theme, isOwner, imgUrl }) => ({
    ...commonStyles(theme, isOwner),
    content: '" "',
    display: 'block',
    border: '6px solid #000000',
    backgroundImage: imgUrl ? `url(${imgUrl})` : 'none',
    backgroundColor: '#000',
    overflow: 'hidden !important',
    '&:hover': {
      border: isOwner ? `6px solid ${theme.colors.primary.main}` : '6px solid #000000',
      '& .set-your-nft': {
        opacity: 1,
      },
    },
  })
);

interface NoImageProps {
  onClick: () => void;
  isOwner: boolean;
  children?: React.ReactNode;
}

const NoImagePlaceholder: React.FC<NoImageProps> = ({
  onClick,
  isOwner,
  children,
  ...otherProps
}) => (
  <PlaceholderWrapper onClick={onClick} isOwner={isOwner} {...otherProps}>
    <svg
      width='98'
      height='125'
      viewBox='0 0 98 125'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M74.2789 55.2528L32.9458 95.7844C33.0904 95.9292 33.2349 95.9292 33.2349 96.0739C33.5239 96.2187 33.813 96.5082 34.102 96.653C34.5356 96.9425 34.9691 97.232 35.2582 97.5215C35.5472 97.6663 35.8363 97.811 36.1253 98.1005C36.5589 98.39 36.9924 98.5348 37.426 98.8243C37.715 98.9691 38.0041 99.1138 38.2931 99.2586C38.7267 99.4033 39.1603 99.6928 39.5938 99.8376C39.8829 99.9824 40.1719 100.127 40.4609 100.127C40.8945 100.272 41.3281 100.417 41.9062 100.561C42.1952 100.706 42.4842 100.706 42.9178 100.851C43.3514 100.996 43.9295 100.996 44.363 101.14C44.6521 101.14 44.9411 101.285 45.3747 101.285C45.9528 101.285 46.3863 101.43 46.9644 101.43C47.2535 101.43 47.5425 101.43 47.8315 101.43C48.6987 101.43 49.4213 101.43 50.2884 101.43C50.722 101.43 51.1555 101.43 51.5891 101.285H51.8781H52.0227C54.335 100.996 56.3583 100.272 58.3816 99.2586C59.1042 98.9691 59.6823 98.5348 60.4049 98.1005C61.1275 97.5215 62.2837 96.7977 63.2953 95.7844L88.7311 70.3074C90.3208 68.7151 91.766 66.8332 93.0667 64.8067L95.379 60.3192C96.9688 56.7003 97.8359 52.7919 97.8359 48.7388C97.8359 35.2764 92.1996 22.3932 82.5167 13.2735C72.6892 4.00916 59.6823 -0.767787 46.0973 0.100749C21.3841 1.40355 1.44019 21.9589 0.428544 46.7122C0.284023 49.6073 0.862108 54.8185 1.58471 59.1612C2.01828 58.4374 2.45184 57.7136 3.02993 56.9898C3.17445 56.8451 3.31897 56.5556 3.46349 56.4108C3.89706 55.8318 4.1861 55.3975 4.61966 54.9632C4.76418 54.8185 4.90871 54.529 5.05323 54.3842C5.63131 53.8052 6.2094 53.0814 6.78748 52.5024L34.102 26.0121C38.0041 22.3932 42.1952 20.6561 47.2535 20.2218C55.0576 19.7876 62.5727 22.3932 68.3536 27.7491L69.6543 28.9072C74.7125 34.1184 77.4584 41.0667 77.4584 48.3045C77.3139 51.0548 76.1577 53.3709 74.2789 55.2528Z'
        fill='white'
      />
      <path
        d='M23.4125 69.583L64.7456 29.1961C64.601 29.0513 64.4565 29.0513 64.312 28.9065C64.0229 28.7618 63.8784 28.617 63.5894 28.4723C63.1558 28.1828 62.7223 27.8933 62.2887 27.6037C61.9996 27.459 61.8551 27.3142 61.5661 27.1695C61.1325 26.88 60.5544 26.5905 60.1209 26.4457C59.8318 26.3009 59.6873 26.1562 59.3983 26.1562C58.8202 25.8667 58.2421 25.5772 57.664 25.4324C57.5195 25.4324 57.375 25.2876 57.2304 25.2876C56.5078 24.9981 55.7852 24.8534 55.0626 24.5639C54.7736 24.4191 54.4845 24.4191 54.051 24.4191C53.6174 24.2744 53.1838 24.2744 52.7503 24.1296C52.4612 24.1296 52.0277 23.9848 51.7386 23.9848C51.3051 23.9848 50.8715 23.8401 50.4379 23.8401C50.1489 23.8401 49.7153 23.8401 49.4263 23.8401C48.7037 23.8401 47.9811 23.8401 47.2585 23.8401C43.0674 24.1296 39.4543 25.7219 36.2749 28.617L9.10484 54.9626C7.94867 56.1207 6.7925 57.4235 5.78085 58.7263C4.7692 60.0291 3.90208 61.4766 3.17947 63.069L2.45686 64.6613C0.867128 68.4249 0 72.3333 0 76.2417C0 89.704 5.63633 102.587 15.3193 111.707C25.1467 120.971 38.1536 125.748 51.7386 124.88C76.3073 123.287 96.3957 102.732 97.2629 77.9788C97.4074 75.0837 96.8293 69.7277 96.1067 65.5298C95.9622 65.6746 95.8176 65.9641 95.6731 66.1088C94.2279 68.2802 92.6382 70.3068 90.7594 72.0438L79.4867 83.4795L65.3236 97.6656C64.0229 98.8237 62.8668 99.6922 61.9996 100.271C59.1092 102.298 55.7852 103.601 52.3167 104.035H51.8832C51.4496 104.035 50.8715 104.18 50.4379 104.18C49.8599 104.18 49.2818 104.18 48.5592 104.18C48.4146 104.18 48.1256 104.18 47.9811 104.18C47.5475 104.18 47.2585 104.18 46.8249 104.18C46.2468 104.18 45.8133 104.035 45.2352 104.035C44.8016 104.035 44.5126 103.89 44.079 103.89C43.5009 103.745 43.0674 103.745 42.4893 103.601C42.0557 103.456 41.7667 103.456 41.3331 103.311C40.755 103.166 40.3214 103.022 39.7434 102.877C39.4543 102.732 39.0208 102.587 38.7317 102.587C38.1536 102.443 37.7201 102.153 37.142 102.008C36.8529 101.864 36.4194 101.719 36.1303 101.574C35.5522 101.285 35.1187 100.995 34.5406 100.706C34.2516 100.561 33.9625 100.416 33.6735 100.271C33.0954 99.9817 32.6618 99.5475 32.0837 99.2579C31.7947 99.1132 31.6502 98.9684 31.3611 98.6789C30.6385 98.0999 29.9159 97.5209 29.1933 96.7971L27.8926 95.639C22.8344 90.4278 20.0885 83.4795 20.0885 76.2417C20.522 73.9256 21.5337 71.4648 23.4125 69.583Z'
        fill='white'
      />
    </svg>
    {children}
  </PlaceholderWrapper>
);

const PlaceholderWrapper = styled('div')<NoImageProps>(({ theme, isOwner }) => ({
  background: '#000',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: `6px solid ${theme.colors.primary.main}`,
  overflow: 'hidden',
  ...commonStyles(theme, isOwner),
  '& svg': {
    height: '60%',
    width: 'auto',
  },
  '&:hover': {
    opacity: 0.85,
    '& .set-your-nft': {
      opacity: 1,
    },
  },
}));

const commonStyles = (theme: Theme, isOwner: boolean): CSSObject => ({
  height: 180,
  width: 180,
  borderRadius: '50%',
  transition: 'opacity .3s',
  cursor: isOwner ? 'pointer' : 'auto',
  opacity: 1,
  flexShrink: 0,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
  overflow: 'hidden',
  WebkitTransform: 'translate3d(0, 0, 0)',
  [theme.min(theme.breakpoints.xs)]: {
    height: 200,
    width: 200,
  },
  [theme.min(theme.breakpoints.sm)]: {
    height: 220,
    width: 220,
  },
  [theme.min(theme.breakpoints.desktop)]: {
    height: theme.pxToVw(261),
    width: theme.pxToVw(261),
  },
  [theme.min(theme.breakpoints.max)]: {
    height: 261,
    width: 261,
  },
});

const SetYourNft = styled('div')<{ isPlaceholder?: boolean }>(
  ({ theme, isPlaceholder }) => ({
    minWidth: '120%',
    marginLeft: isPlaceholder ? 0 : '-10%',
    position: 'absolute',
    paddingBottom: 30,
    bottom: -20,
    // zIndex: theme.zIndex.xl,
    backgroundColor: theme.colors.primary.main,
    fontSize: 12,
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 8,
    opacity: 0.4,
    transition: 'all .4s',
    [theme.min(theme.breakpoints.md)]: {
      paddingBottom: 30,
      fontSize: 16,
      paddingTop: 12,
      bottom: -10,
    },
    [theme.betweenDM]: {
      paddingBottom: theme.pxToVw(30),
      fontSize: theme.pxToVw(16),
      paddingTop: theme.pxToVw(12),
      bottom: theme.pxToVw(-10),
    },
  })
);
